import React, { useMemo, useState } from 'react';
import { View, StyleSheet, Text, TouchableOpacity, ScrollView, Linking, Platform, useWindowDimensions } from 'react-native';
import { useAppSelector } from '@/store';
import { UserType } from '@/types/residents/user';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { DesktopHeaderCard } from '@/components/residents/desktop/DesktopHeaderCard';
import { commonErrorMessages, FAQ_EXPAND_ICON_COLOR, FaqSection, faqSectionsGuarantor, faqSectionsResident } from './faq';
import { strings } from '@/constants/residents';

type DesktopFAQScreenProps = {};

const SectionRow = ({ section }: { section: FaqSection }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <View style={styles.sectionRow}>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>{section.title}</Text>
        <TouchableOpacity accessibilityRole="button" onPress={() => setExpanded(!expanded)}>
          {expanded ? (
            <RESIDENTS_SVG_COMPONENTS.CIRCLE_MINUS width={24} height={24} color={FAQ_EXPAND_ICON_COLOR} />
          ) : (
            <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={24} height={24} color={FAQ_EXPAND_ICON_COLOR} />
          )}
        </TouchableOpacity>
      </View>
      {expanded && <Text style={styles.sectionSubtext}>{section.subtext}</Text>}
    </View>
  );
};

const CommonErrors = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <View style={styles.sectionRow}>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>Common Error Messages</Text>
        <TouchableOpacity accessibilityRole="button" onPress={() => setExpanded(!expanded)}>
          {expanded ? (
            <RESIDENTS_SVG_COMPONENTS.CIRCLE_MINUS width={24} height={24} color={FAQ_EXPAND_ICON_COLOR} />
          ) : (
            <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={24} height={24} color={FAQ_EXPAND_ICON_COLOR} />
          )}
        </TouchableOpacity>
      </View>
      {expanded && (
        <View style={styles.errorTable}>
          {commonErrorMessages.map((row, idx) => {
            const isHeader = idx === 0;
            const isLast = idx === commonErrorMessages.length - 1;
            return (
              <View key={idx} style={[styles.errorRow, isHeader && styles.errorRowHeader, isLast && styles.errorRowLast]}>
                <Text style={[
                  styles.errorCell,
                  styles.errorCellCode,
                  !isHeader && styles.errorCellCodeDivider,
                  isHeader && styles.errorHeaderText,
                ]}>{row[0]}</Text>
                <Text style={[styles.errorCell, styles.errorCellAction, isHeader && styles.errorHeaderText]}>{row[1]}</Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

export const DesktopFAQScreen: React.FC<DesktopFAQScreenProps> = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1280; // match `isMinScreenXl`

  const sections = useMemo<FaqSection[]>(() => {
    if (!user) return faqSectionsResident;
    // Resident-like types get resident FAQ; others get guarantor FAQ
    return user.type === UserType.RESIDENT ? faqSectionsResident : faqSectionsGuarantor;
  }, [user]);

  const onSupportPress = () => {
    const email = user?.email || '';
    const subject = encodeURIComponent("Support request");
    const body = encodeURIComponent(
      `Hi Resio Support,%0D%0A%0D%0AI'm looking for help with my account.%0D%0A%0D%0AThanks,%0D%0A${email}`
    );
    const url = `mailto:support@resio.com?subject=${subject}&body=${body}`;
    Linking.openURL(url).catch(() => {});
  };

  return (
    <View style={styles.container}>
      <DesktopHeaderCard
        icon={<RESIDENTS_SVG_COMPONENTS.QUESTION_BUBBLE width={64} height={64} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />}
        title={strings.faqCard.cardTitle}
        subtitleLines={[
          strings.faqCard.cardSubtitle,
          strings.faqCard.cardSubtitle2,
        ]}
        ctaText={isDesktop ? strings.faqCard.cardCta : undefined}
        onPressCta={isDesktop ? onSupportPress : undefined}
      />
      {!isDesktop && (
        <TouchableOpacity accessibilityRole="button" onPress={onSupportPress} style={styles.supportButtonMobile}>
          <Text style={styles.supportButtonText}>{strings.faqCard.cardCta}</Text>
        </TouchableOpacity>
      )}

      <ScrollView
        style={styles.scrollArea}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
      >
        {sections.map((section, idx) => (
          <SectionRow key={idx} section={section} />
        ))}
        <CommonErrors />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    paddingHorizontal: 18,
    paddingVertical: 24,
  },
  headerCard: {},
  headerTextCol: {},
  headerTitle: {},
  headerSubtitle: {},
  supportButtonDesktop: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  supportButtonMobile: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginTop: 12,
  },
  supportButtonText: {
    color: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    fontWeight: '600',
    fontSize: 14,
  },
  scrollArea: {
    flex: 1,
    marginTop: 8,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  sectionRow: {
    marginHorizontal: 8,
    paddingVertical: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 18,
    fontWeight: '700',
    flex: 1,
    paddingRight: 12,
  },
  expandIcon: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    fontSize: 24,
    lineHeight: 24,
    paddingHorizontal: 8,
  },
  sectionSubtext: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
    marginTop: 8,
  },
  errorTable: {
    marginTop: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    borderRadius: 8,
    overflow: 'hidden',
  },
  errorRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  errorRowHeader: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  errorRowLast: {
    borderBottomWidth: 0,
  },
  errorCell: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 13,
  },
  errorHeaderText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
    textAlign: 'center',
  },
  errorCellCode: {
    flex: 2,
    paddingRight: 12,
  },
  errorCellCodeDivider: {
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    paddingRight: 12,
    marginRight: 12,
  },
  errorCellAction: {
    flex: 3,
  },
});

